import { useState } from "react";
import { Link } from "react-router-dom";
import { FiGlobe, FiGithub } from "react-icons/fi";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { healthProjects, webdevProjects } from "../data/projects";

const LINK_ICONS = { live: FiGlobe, github: FiGithub };

const TABS = [
  { key: "all", label: "All" },
  { key: "health", label: "Health & Research" },
  { key: "webdev", label: "Web Development" },
];

export default function Projects() {
  const [filter, setFilter] = useState("all");
  const showHealth = filter === "all" || filter === "health";
  const showWebdev = filter === "all" || filter === "webdev";

  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pb-10 pt-32 md:px-12 md:pt-40">
        <p className="eyebrow">All Work</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold leading-tight text-heading md:text-5xl">
          Projects &amp; Initiatives
        </h1>
        <p className="mt-5 max-w-[52ch] text-[0.95rem] leading-relaxed text-ink-soft">
          A collection of health research, community programs, and web development projects spanning both
          my clinical and technical practice.
        </p>
      </div>

      <div className="sticky top-[65px] z-30 border-y border-line bg-paper/95 backdrop-blur">
        <div className="mx-auto flex max-w-content gap-8 overflow-x-auto px-6 md:px-12">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`whitespace-nowrap border-b-2 py-4 text-[0.75rem] uppercase tracking-widest2 transition-colors ${
                filter === t.key
                  ? "border-forest-900 text-heading"
                  : "border-transparent text-ink-faint hover:text-ink-soft"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {showHealth && (
        <section className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-20">
          <p className="eyebrow">Health &amp; Research</p>
          <h2 className="mt-2 font-serif text-2xl font-extrabold text-heading md:text-3xl">
            Clinical &amp; Community Initiatives
          </h2>
          <div className="mt-10 divide-y divide-line border-t border-line">
            {healthProjects.map((p, i) => (
              <Reveal key={p.title} delay={i * 60} className="grid gap-2 py-8 md:grid-cols-[1fr_2fr] md:gap-10">
                <div>
                  <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-500">{p.tag}</p>
                  <p className="mt-2 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">{p.foot}</p>
                </div>
                <div>
                  <h3 className="font-serif text-xl text-heading">{p.title}</h3>
                  <p className="mt-2 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">{p.desc}</p>
                  {p.detailPath && (
                    <Link
                      to={p.detailPath}
                      className="mt-4 inline-block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading"
                    >
                      Read more &rarr;
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {showWebdev && (
        <section className="border-t border-line bg-surface-alt">
          <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-20">
            <p className="eyebrow">Web Development</p>
            <h2 className="mt-2 font-serif text-2xl font-extrabold text-heading md:text-3xl">
              Intern &amp; Personal Projects
            </h2>
            <div className="mt-10 divide-y divide-line border-t border-line">
              {webdevProjects.map((p, i) => (
                <Reveal key={p.title} delay={i * 60} className="grid gap-3 py-8 md:grid-cols-[1fr_2fr] md:gap-10">
                  <div>
                    <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-500">{p.tag}</p>
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-heading">{p.title}</h3>
                    <p className="mt-2 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">{p.desc}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                      {p.links.map((l) => {
                        const Icon = LINK_ICONS[l.type];
                        return (
                          <a
                            key={l.label}
                            href={l.href}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 text-[0.75rem] uppercase tracking-widest2 text-ink-soft hover:text-heading"
                          >
                            {Icon && <Icon aria-hidden="true" />}
                            {l.label}
                          </a>
                        );
                      })}
                      {p.detailPath && (
                        <Link
                          to={p.detailPath}
                          className="text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading"
                        >
                          Details &rarr;
                        </Link>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}
